import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['button']

  async syncInvoice(event) {
    const button = event.currentTarget
    const eventId = button.dataset.eventId
    const originalText = button.querySelector('span').textContent
    const buttonSpan = button.querySelector('span')

    // Disable button and show loading state
    button.disabled = true
    button.classList.add('opacity-75', 'cursor-not-allowed')
    buttonSpan.textContent = 'Syncing...'

    // Show loading notification
    const loadingNotification = this.showNotification(
      'Syncing to Xero...',
      'info',
      false
    )

    try {
      // Ask for optional Xero details (simple prompt-based modal for now)
      const today = new Date()
      const pad = (n) => (n < 10 ? `0${n}` : `${n}`)
      const defaultIssue = `${today.getFullYear()}-${pad(
        today.getMonth() + 1
      )}-${pad(today.getDate())}`
      const twoWeeks = new Date(today.getTime() + 14 * 86400000)
      const defaultExpiry = `${twoWeeks.getFullYear()}-${pad(
        twoWeeks.getMonth() + 1
      )}-${pad(twoWeeks.getDate())}`

      let issueDate = defaultIssue
      let expiryDate = defaultExpiry

      // Choose what to create in Xero (defaults to a draft invoice).
      let documentType = 'invoice'
      const typeInput = prompt(
        "Create in Xero as 'invoice' or 'quote'?",
        'invoice'
      )
      if (typeInput && typeInput.trim().toLowerCase() === 'quote') {
        documentType = 'quote'
      }

      // Always show prompts for dates
      const i = prompt('Issue date (YYYY-MM-DD)', defaultIssue)
      if (i && /^\d{4}-\d{2}-\d{2}$/.test(i)) issueDate = i
      const e = prompt(
        documentType === 'invoice'
          ? 'Due date (YYYY-MM-DD)'
          : 'Expiry date (YYYY-MM-DD)',
        defaultExpiry
      )
      if (e && /^\d{4}-\d{2}-\d{2}$/.test(e)) expiryDate = e

      // Ask for quote price source (optional)
      // Always ask for quote price source
      let quotePriceSource
      let customQuotePrice
      const src = prompt(
        "Quote price source (type: 'budget', 'total', or 'custom')",
        'budget'
      )
      if (src) quotePriceSource = src.trim().toLowerCase()
      if (quotePriceSource === 'custom') {
        const amt = prompt('Enter custom quote amount (numbers only)', '0')
        if (amt && !isNaN(Number(amt))) customQuotePrice = Number(amt)
      }

      const response = await fetch(`/events/${eventId}/sync_to_xero`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          xero: {
            issue_date: issueDate,
            expiry_date: expiryDate,
            document_type: documentType,
            quote_price_source: quotePriceSource,
            quote_price: customQuotePrice,
          },
        }),
      })

      // Safely parse JSON or fall back to text (e.g., HTML error pages)
      let data
      const ct = response.headers.get('content-type') || ''
      if (ct.includes('application/json')) {
        data = await response.json()
      } else {
        const text = await response.text()
        data = {
          success: response.ok,
          message: text || `HTTP ${response.status}`,
        }
      }

      // Remove loading notification
      if (loadingNotification) loadingNotification.remove()

      if (data.success) {
        // Show success message
        buttonSpan.textContent = '✓ Synced!'
        button.classList.remove('bg-blue-500', 'hover:bg-blue-600')
        button.classList.add('bg-green-500', 'hover:bg-green-600')

        // Show success notification with details + a link straight to Xero
        const successMessage =
          data.message || `Draft ${documentType} created in Xero`
        this.showNotification(successMessage, 'success', true, data.xero_url)

        // Reload page after 2 seconds to show updated badges/links
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      } else {
        // Show error message
        buttonSpan.textContent = 'Sync Failed'
        button.classList.remove('bg-blue-500', 'hover:bg-blue-600')
        button.classList.add('bg-red-500', 'hover:bg-red-600')

        const errorMessage = data.message || 'Failed to sync to Xero'
        this.showNotification(errorMessage, 'error')

        // Reset button after 5 seconds
        setTimeout(() => {
          buttonSpan.textContent = originalText
          button.classList.remove('bg-red-500', 'hover:bg-red-600')
          button.classList.add('bg-blue-500', 'hover:bg-blue-600')
          button.disabled = false
          button.classList.remove('opacity-75', 'cursor-not-allowed')
        }, 5000)
      }
    } catch (error) {
      // Remove loading notification
      if (loadingNotification) loadingNotification.remove()

      buttonSpan.textContent = 'Sync Failed'
      button.classList.remove('bg-blue-500', 'hover:bg-blue-600')
      button.classList.add('bg-red-500', 'hover:bg-red-600')

      this.showNotification(
        'Network error while syncing to Xero. Please check your connection.',
        'error'
      )

      // Reset button after 5 seconds
      setTimeout(() => {
        buttonSpan.textContent = originalText
        button.classList.remove('bg-red-500', 'hover:bg-red-600')
        button.classList.add('bg-blue-500', 'hover:bg-blue-600')
        button.disabled = false
        button.classList.remove('opacity-75', 'cursor-not-allowed')
      }, 5000)
    }
  }

  async syncSupplierBills(event) {
    const button = event.currentTarget
    const eventId = button.dataset.eventId
    const originalText = button.querySelector('span').textContent
    const buttonSpan = button.querySelector('span')

    // Disable button and show loading state
    button.disabled = true
    button.classList.add('opacity-75', 'cursor-not-allowed')
    buttonSpan.textContent = 'Syncing Bills...'

    // Show loading notification
    const loadingNotification = this.showNotification(
      'Syncing supplier bills to Xero...',
      'info',
      false
    )

    try {
      const response = await fetch(
        `/events/${eventId}/sync_supplier_bills_to_xero`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]')
              .content,
          },
        }
      )

      const data = await response.json()

      // Remove loading notification
      if (loadingNotification) loadingNotification.remove()

      if (data.success) {
        // Show success message
        buttonSpan.textContent = '✓ Bills Synced!'
        button.classList.remove('bg-purple-500', 'hover:bg-purple-600')
        button.classList.add('bg-green-500', 'hover:bg-green-600')

        // Show success notification with details
        this.showNotification(data.message, 'success')

        // Reload page after 2 seconds
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      } else {
        // Show error message
        buttonSpan.textContent = 'Sync Failed'
        button.classList.remove('bg-purple-500', 'hover:bg-purple-600')
        button.classList.add('bg-red-500', 'hover:bg-red-600')

        this.showNotification(data.message, 'error')

        // Reset button after 5 seconds
        setTimeout(() => {
          buttonSpan.textContent = originalText
          button.classList.remove('bg-red-500', 'hover:bg-red-600')
          button.classList.add('bg-purple-500', 'hover:bg-purple-600')
          button.disabled = false
          button.classList.remove('opacity-75', 'cursor-not-allowed')
        }, 5000)
      }
    } catch (error) {
      // Remove loading notification
      if (loadingNotification) loadingNotification.remove()

      buttonSpan.textContent = 'Sync Failed'
      button.classList.remove('bg-purple-500', 'hover:bg-purple-600')
      button.classList.add('bg-red-500', 'hover:bg-red-600')

      this.showNotification(
        'Network error while syncing supplier bills. Please try again.',
        'error'
      )

      // Reset button after 5 seconds
      setTimeout(() => {
        buttonSpan.textContent = originalText
        button.classList.remove('bg-red-500', 'hover:bg-red-600')
        button.classList.add('bg-purple-500', 'hover:bg-purple-600')
        button.disabled = false
        button.classList.remove('opacity-75', 'cursor-not-allowed')
      }, 5000)
    }
  }

  showNotification(message, type, autoDismiss = true, linkUrl = null) {
    // Create notification element
    const notification = document.createElement('div')

    // Determine colors based on type
    let bgColor, icon
    switch (type) {
      case 'success':
        bgColor = 'bg-green-500'
        icon = '✓'
        break
      case 'error':
        bgColor = 'bg-red-500'
        icon = '✗'
        break
      case 'info':
        bgColor = 'bg-blue-500'
        icon = '⟳'
        break
      default:
        bgColor = 'bg-gray-500'
        icon = 'ℹ'
    }

    notification.className = `fixed bottom-4 right-4 p-4 rounded-md shadow-lg text-sm z-[9999] ${bgColor} text-white max-w-md animate-fade-in-down flex items-center gap-2`
    notification.innerHTML = `<span class="font-bold text-lg">${icon}</span><span>${message}</span>`

    // Optional "View in Xero" link (e.g. straight to the synced document)
    if (linkUrl) {
      const link = document.createElement('a')
      link.href = linkUrl
      link.target = '_blank'
      link.rel = 'noopener'
      link.textContent = 'View in Xero'
      link.className = 'underline font-semibold whitespace-nowrap ml-1'
      notification.appendChild(link)
    }

    document.body.appendChild(notification)

    // Auto-dismiss after 5 seconds if enabled
    if (autoDismiss) {
      setTimeout(() => {
        notification.classList.add('animate-fade-out-down')
        notification.addEventListener('animationend', () => {
          notification.remove()
        })
      }, 5000)
    }

    return notification
  }
}
